// Reusable JSON-LD schema builders for SEO
import { REVIEWS, reviewsAggregate } from "@/data/reviewsData";
import { COMPANY, REGIONAL_CONTACTS } from "@/data/clientsData";

export const SITE_URL = "https://stagehookglobal.com";
export const LOGO = `${SITE_URL}/logo512.png`;
export const LINKEDIN = "https://www.linkedin.com/company/stagehookglobal/";
export const INSTAGRAM = "https://www.instagram.com/stagehookglobal";

// Site-wide Organization schema (used everywhere)
export const organizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${SITE_URL}/#organization`,
  name: COMPANY.name,
  legalName: "Stagehook Global",
  url: SITE_URL,
  logo: LOGO,
  description: COMPANY.description,
  slogan: COMPANY.tagline,
  email: COMPANY.email,
  telephone: COMPANY.phone,
  foundingDate: "2015",
  knowsAbout: [
    "Exhibition Stall Design",
    "Trade Show Booth Fabrication",
    "Modular Exhibition Stands",
    "Custom Exhibition Stands",
    "Turnkey Booth Solutions",
    "Exhibition Graphics and Branding",
    "On-site Installation and Dismantling",
    "Exhibition Logistics",
  ],
  areaServed: [
    { "@type": "Country", name: "United Kingdom" },
    { "@type": "Country", name: "United States" },
    { "@type": "Place", name: "Europe" },
    { "@type": "Country", name: "Russia" },
    { "@type": "Place", name: "Asia" },
  ],
  address: REGIONAL_CONTACTS.map((c) => ({
    "@type": "PostalAddress",
    streetAddress: c.address,
    addressCountry: c.region,
  })),
  contactPoint: REGIONAL_CONTACTS.map((c) => ({
    "@type": "ContactPoint",
    telephone: c.phone,
    contactType: "sales",
    areaServed: c.region,
    availableLanguage: ["English"],
  })),
  sameAs: [LINKEDIN, INSTAGRAM],
});

// Website schema with SearchAction
export const websiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  url: SITE_URL,
  name: COMPANY.name,
  description: COMPANY.description,
  publisher: { "@id": `${SITE_URL}/#organization` },
  inLanguage: "en",
});

export const localBusinessSchema = () => ({
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  "@id": `${SITE_URL}/#business`,
  name: COMPANY.name,
  image: LOGO,
  url: SITE_URL,
  telephone: COMPANY.phone,
  email: COMPANY.email,
  priceRange: "$$$",
  description: COMPANY.description,
  areaServed: ["United Kingdom", "United States", "Europe", "Russia", "Asia"],
  serviceType: [
    "Exhibition Stall Design",
    "Exhibition Stand Fabrication",
    "Expo Booth Builder",
    "Trade Fair Stand Contractor",
    "Turnkey Exhibition Services",
  ],
  ...(reviewsAggregate()
    ? {
        aggregateRating: { "@type": "AggregateRating", ...reviewsAggregate(), bestRating: 5, worstRating: 1 },
        review: REVIEWS.map((r) => ({
          "@type": "Review",
          author: { "@type": "Organization", name: r.author },
          datePublished: r.date,
          reviewBody: r.text,
          reviewRating: { "@type": "Rating", ratingValue: r.rating, bestRating: 5 },
        })),
      }
    : {}),
});

export const breadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: item.name,
    item: `${SITE_URL}${item.path}`,
  })),
});

export const itemListSchema = (name, items) => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  name,
  numberOfItems: items.length,
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    url: `${SITE_URL}${it.path}`,
    name: it.name,
  })),
});

// Individual expo / event schema
export const eventServiceSchema = (expo) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: `${expo.name} Stand Builder & Exhibition Stall Designer`,
  provider: { "@id": `${SITE_URL}/#organization` },
  areaServed: {
    "@type": "City",
    name: expo.city,
    containedInPlace: { "@type": "Country", name: expo.countryName },
  },
  description: `${COMPANY.name} builds hero exhibition stands at ${expo.name} in ${expo.city}, ${expo.countryName}. ${expo.description}`,
  url: `${SITE_URL}/expo/${expo.slug}`,
  image: [...new Set([expo.heroImage, ...(expo.clients || []).map((c) => c.image)].filter(Boolean))].map((s) => (s.startsWith("http") ? s : `${SITE_URL}${s}`)),
  offers: {
    "@type": "Offer",
    availability: "https://schema.org/InStock",
    priceCurrency: "EUR",
    url: `${SITE_URL}/contact`,
    description: "Custom exhibition stand quote in 72 hours",
  },
});

export const videoObjectSchema = (expo) => ({
  "@context": "https://schema.org",
  "@type": "VideoObject",
  name: `${expo.name} stall handover — Stagehook Global`,
  description: `Exhibition stall handover video at ${expo.name}, ${expo.city}, ${expo.countryName}. ${expo.description}`,
  thumbnailUrl: expo.heroImage?.startsWith("http")
    ? expo.heroImage
    : `${SITE_URL}${expo.heroImage}`,
  contentUrl: expo.video?.startsWith("http")
    ? expo.video
    : `${SITE_URL}${expo.video}`,
  uploadDate: "2026-08-30",
  publisher: { "@id": `${SITE_URL}/#organization` },
});

export const faqSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.question,
    acceptedAnswer: { "@type": "Answer", text: f.answer },
  })),
});
